import {
  AioModule,
  PieceOfContent,
  FAQ,
  PreComparisonChecklist,
  SimilarProducts,
  ScenarioAccordion,
  QuoteHighlight,
  ImageCarousel,
} from '@hyphengroup/cms-components';
import { blockKey } from '@hyphengroup/cms-components/calculatedAttributes/blockKey';
import { CalculationScope } from '@hyphengroup/cms-components/calculatedAttributes/store';
import { CompareListingProvider } from '@hyphengroup/cms-components/cms-blocks/Compare';
import { WhatsappStickyModule } from '@hyphengroup/cms-components/cms-blocks/ContactUs';
import { EmbeddedScript } from '@hyphengroup/cms-components/cms-blocks/EmbeddedScript';
import { PreferredSourceButton } from '@hyphengroup/cms-components/cms-blocks/PreferredSourceButton';
import { ShortDisclaimer } from '@hyphengroup/cms-components/components/Disclaimer/ShortDisclaimer';
import classNames from 'classnames';
import { SimpleStickyFooter } from 'components/SimpleStickyFooter';
import { Fragment } from 'react';
import { BrandId } from '../../../../utils/override';
import { useClientContext } from '../../../modules/context/client';
import {
  PageContentContainer,
  PageContentContainerType,
} from '../../PageContentContainer';
import { AboutModule } from '../components-user/AboutModule';
import { BenefitComparisonTableWrapper } from '../components-user/BenefitComparisonTable/BenefitComparisonTableWrapper';
import { BlueCTA } from '../components-user/BlueCTA';
import { CallToAction } from '../components-user/CallToAction';
import CardListingWrapper from '../components-user/CardListing/CardListingWrapper';
import CcCardListingWrapper from '../components-user/CcCardListing/CcCardListingWrapper';
import ComparisonTableMap from '../components-user/ComparisonTableMap';
import { CounterModule } from '../components-user/CounterModule';
import { CtaSectionBlock } from '../components-user/CtaSectionBlock/CtaSectionBlock';
import { CustomerReviewsBlock } from '../components-user/CustomerReviewsBlock/CustomerReviewsBlock';
import { ExpandableCard } from '../components-user/ExpandableCard';
import { FeedbackAndShare } from '../components-user/FeedbackAndShare';
import { HeaderWithTabs } from '../components-user/HeaderWithTabs';
import { Heading } from '../components-user/Heading';
import { HeroCampaingFeature } from '../components-user/HeroCampaingFeature';
import { ArticlesHPModule } from '../components-user/HP/ArticlesModule';
import { NewsletterSubscription } from '../components-user/HP/NewsletterSubcription';
import { HPReviewCards } from '../components-user/HPReviewCards';
import { ImageCmp } from '../components-user/ImageCmp';
import { InfoCards } from '../components-user/InfoCards';
import { InfoCardsFAQ } from '../components-user/InfoCardsFAQ';
import { Paragraph } from '../components-user/Paragraph';
import { PersonalLoanCalculator } from '../components-user/PersonalLoanCalculator';
import { PlanTierSelectorWrapper } from '../components-user/PlanTierSelector/PlanTierSelectorWrapper';
import { PolicySummaryCardWrapper } from '../components-user/PolicySummaryCard/PolicySummaryCardWrapper';
import { ProductCardGridComponent } from '../components-user/ProductCardGrid/ProductCardGrid';
import { ProviderList } from '../components-user/ProviderList';
import { RedirectTiles } from '../components-user/RedirectTiles';
import { ReviewCards } from '../components-user/ReviewCard';
import { RewardCardGridComponent } from '../components-user/RewardCardGrid/RewardCardGrid';
import { SecondaryCTA } from '../components-user/SecondaryCTA/SecondaryCTA';
import { SelectedProducts } from '../components-user/SelectedProducts';
import { StandoutTextBlock } from '../components-user/StandoutTextBlock';
import { TheWhiteBoard } from '../components-user/TheWhiteBoard';
import { TopPicksModule } from '../components-user/TopPicksModule';
import TravelPolicySearch from '../components-user/travel/TravelPolicySearch/TravelPolicySearch';
import { TrustStripWrapper } from '../components-user/TrustStrip/TrustStripWrapper';
import { VerticalArticles } from '../components-user/VerticalArticles/VerticalArticles';
import { VideoBlock } from '../components-user/VideoBlock/VideoBlock';
import { getBlockSkipKey } from '../helpers/blockSkipKey';
import { isVisibleBlock } from '../helpers/componentTree';
import { getProductBlockVertical } from '../helpers/productBlockVertical';
import { pushDataLayer } from '../pushDataLayer';
import { CmsComponentProps, PageType, VerticalType } from '../types';
import { AudienceGate } from './AudienceGate';
import { CollapsibleContentTabbed } from './CollapsibleContentTabbed/CollapsibleContentTabbed';
import { ProsAndCons } from './ComparisonTable/ProsAndCons';
import { FeaturesGridModule } from './FeaturesGridModule/FeaturesGridModule';
import { LogoCarousel } from './LogoCarouselModule';
import { MiniProductHighlight } from './MiniProductHighlight/MiniProductHighlight';
import { MultiProductBlockWrapper } from './MultiProductBlock/MultiProductBlockWrapper';
import { PortalPlace } from './PortalPlace';
import { PrefilledBlock } from './PrefiledBlock';
import { ProgressStepperWrapper } from './ProgressStepperWrapper';
import { QuickAnswerWrapper } from './QuickAnswer/QuickAnswerWrapper';
import { SectionalHeader } from './SectionalHeaderModule/SectionalHeaderModule';
import { SingleProductPromotionWrapper } from './SingleProductPromotion/SingleProductPromotionWrapper';
import SummaryCards from './Summary/SummaryCards';
import { TableOfContents } from './Summary/TableOfContents';
import { TableBuilderWrapper } from './TableBuilderWrapper';
import {
  ComponentProductMetadata,
  UrlPrefixEnum,
} from '$/(cms)/_modules/cmsService/types';
import {
  CmsPageAddressFlags,
  getCmsAbsPageLink,
} from '$/(cms)/_modules/routing';
import { ProductDetailsBlock } from '$/components/cms/components-internal/ProductDetails/ProductDetailsBlock';
import { AnchorStickyFooterComponent } from '$/components/cms/components-user/AnchorStickyFooterComponent';
import CreditCardPromotion from '$/components/cms/components-user/CreditCardPromotion';
import { RelevantArticle } from '$/components/cms/components-user/RelevantArticle';
import { RpCategories } from '$/components/cms/components-user/RpCategories';
import { SwiperCards } from '$/components/cms/components-user/SwiperCards';
import { InsuranceStepper } from '$/components/InsuranceStepper';
import { SearchStickyFooter } from '$/components/SearchStickyFooter';

export interface BlockMapperProps {
  /** The blocks this instance renders. */
  components: Array<CmsComponentProps>;
  /**
   * The whole page's blocks, for the cross-block lookups (ProductMetaConfig,
   * modal details, sticky region, Table of Contents). Defaults to `components`,
   * which every caller relies on today — a nested block inside `PrefilledBlock`
   * therefore resolves against that block's own list, not the page's.
   */
  baseComponents?: Array<CmsComponentProps>;
  /**
   * Inclusive range of visible components to render, for a caller laying blocks
   * out in more than one container. Indices are positions in the same list
   * `content-${index}` counts, so anchors survive wherever the bounds fall.
   */
  renderRange?: [number, number];
  /**
   * Which half of each block to render: a block is its sticky portal roots plus
   * the block itself, and a column layout needs the roots at full page width.
   */
  render?: 'all' | 'portals' | 'blocks';
  vertical?: VerticalType;
  pageType: PageType;
  slug?: string;
  urlPrefix?: UrlPrefixEnum;
  /** The page's root flags, so the share URL names its root (`getCmsPageAddressFlags`). */
  addressFlags?: CmsPageAddressFlags;
  index?: number;
  isHomePage?: boolean;
  ContentContainer?: PageContentContainerType;
  /**
   * Component skip keys (see `getBlockSkipKey` — `${__typename}:${id}`) to
   * skip rendering while preserving the position-based index used for
   * `content-${index}` anchors. Use when a component is rendered outside
   * this mapper (e.g. hoisted into the article header slot) but its original
   * position must not shift the indexes of subsequent components, which are
   * referenced by Strapi TOC `selected_blocks.index` and by
   * AnchorStickyFooter `content-N` targets.
   *
   * Keyed on (typename, id) rather than bare id: Strapi ids are unique only
   * within a component type, so two different block types can legitimately
   * share the same id on the same page — a bare-id skip list would then drop
   * the wrong block as collateral damage.
   */
  skipKeys?: string[];
  /**
   * Like `skipKeys`, but for a block that renders nowhere (not hoisted): it
   * keeps its `content-${index}` slot and takes no GA `component_index` row.
   */
  hiddenKeys?: string[];
}

/**
 * Homepage blocks lay themselves out (PageContent passes no container there);
 * a library block that doesn't gets the page gutter from here.
 */
const selfContainedOnHomepage = [
  'ComponentPagesComponentPreferredSourceButtonComponent',
];

const getContentContainer = (props: {
  typename: string;
  ContentContainer?: PageContentContainerType;
  isHomePage?: boolean;
}) => {
  const { typename, ContentContainer, isHomePage } = props;

  if (isHomePage && selfContainedOnHomepage.includes(typename)) {
    return PageContentContainer;
  }

  const fullWidthBlocks = [
    // TODO It should be uncommented when the hack with absolute positioning in the component is removed

    // 'ComponentPagesComponentPromotionCampaings',
    // 'ComponentPagesComponentNestedContent',
    // 'ComponentPagesComponentHeroCampaingFeature',
    'ComponentPagesComponentInsuranceStepperComponent',
    'ComponentPagesComponentRpCategories',
    'ComponentPagesComponentPrefilledBlock',
    'ComponentPagesComponentFlexibleHeroBlockComponent',
    'ComponentPagesComponentGridModuleComponent',
    'ComponentPagesComponentCustomerReviewsBlockComponent',
    'ComponentPagesComponentCtaSectionBlockComponent',
    'ComponentPagesComponentQuoteHighlightComponent',
    'ComponentPagesProviderList',
    'ProductDetailsComponent',
    'SingleProductPromotionsComponent',
  ];

  if (fullWidthBlocks.includes(typename)) {
    return;
  }

  return ContentContainer;
};

/**
 * Module scope on purpose: declared inside `BlockMapper` it would be a new
 * component type each render, remounting every block subtree.
 */
export const PlainContainer = (c: JSX.IntrinsicElements['div']) => (
  <div {...c} />
);

/**
 * The list `renderRange` indexes into, and the one `content-${index}` counts.
 *
 * Exported because `SortAndFilterLayout` computes those bounds: the two must
 * apply the same filtering or the bounds point at other blocks, so there is one
 * definition rather than two that agree by memory.
 */
export const getVisibleBlocks = (
  components: Array<CmsComponentProps>,
): Array<CmsComponentProps> =>
  components.filter(
    (c) =>
      isVisibleBlock(c) &&
      c.__typename !== 'ComponentPagesComponentProductMetaConfig',
  );

export const BlockMapper: React.FC<BlockMapperProps> = (props) => {
  const { brand, langPrefix, host } = useClientContext();
  const {
    components,
    baseComponents = components,
    renderRange,
    render = 'all',
    vertical,
    pageType,
    slug,
    isHomePage,
    ContentContainer,
    urlPrefix,
    skipKeys,
    hiddenKeys,
  } = props;
  const skipKeySet = new Set(skipKeys ?? []);
  const hiddenKeySet = new Set(hiddenKeys ?? []);
  const pathname = getCmsAbsPageLink({
    brand,
    host: host,
    pageType,
    vertical,
    slug,
    langPrefix,
    urlPrefix,
    ...props.addressFlags,
  });

  const metadataProductCard = baseComponents.find(
    (c) => c.__typename === 'ComponentPagesComponentProductMetaConfig',
  );
  // Card Listing / Comparison Table follow their products' vertical (CMS-1987);
  // the PL filter slot they portal into has to be gated the same way.
  const productBlockVertical =
    getProductBlockVertical(metadataProductCard?.product_card) ?? vertical;
  const productBlockTypenames = [
    'ComponentPagesComponentCardListingPosition',
    'ComponentPagesComponentComparisonTable',
  ];

  const productDetailsComponentsKeys: string[] = baseComponents.flatMap((c) =>
    c.__typename === 'ProductDetailsComponent' &&
    c.renderingMode === 'MODAL' &&
    c.visible &&
    c.product?.title
      ? [c.product.title]
      : [],
  );

  const visibleComponents = getVisibleBlocks(components);
  if (!visibleComponents.length) return null;

  // The page's list under the same filtering, so the sticky-region search sees
  // every product block and not only the ones in this range.
  const visibleBaseComponents =
    baseComponents === components
      ? visibleComponents
      : getVisibleBlocks(baseComponents);

  const [rangeStart, rangeEnd] = renderRange ?? [
    0,
    visibleComponents.length - 1,
  ];
  // An empty range renders nothing at all rather than an empty stack, which
  // would still be a flex child and so add one gap to the layout around it.
  if (rangeStart > rangeEnd) return null;

  // GA component_index = the block's visible row: skipKeys components render
  // above the mapper (hoisted), so they take the first rows (CMS-1693 QA).
  // The slot-based `index` still feeds content-N anchors and the Strapi TOC.
  const hoistedCount = visibleComponents.filter((p) =>
    skipKeySet.has(getBlockSkipKey(p)),
  ).length;
  let renderedBefore = 0;
  const gaIndexBySlot = visibleComponents.map((p) =>
    skipKeySet.has(getBlockSkipKey(p)) || hiddenKeySet.has(getBlockSkipKey(p))
      ? -1
      : hoistedCount + renderedBefore++,
  );

  const stackClassName = classNames({
    'flex flex-col': isHomePage,
    'flex flex-col gap-spacing/gap/y': !isHomePage,
  });

  // Two pieces per block, so a column layout can take the roots at full page
  // width and the block inside a column. See the `render` prop.
  //
  // Only the range is walked, but `index` stays the position in the whole list:
  // it is derived from `rangeStart`, not from the loop, because `content-${index}`
  // and the GA index have to agree across every caller that renders a range.
  const renderedBlocks = visibleComponents
    .slice(rangeStart, rangeEnd + 1)
    .map((p, offset) => {
      const index = rangeStart + offset;
      // Skip but keep the index slot — see the `skipKeys` / `hiddenKeys` JSDoc.
      const slotKey = getBlockSkipKey(p);
      if (skipKeySet.has(slotKey) || hiddenKeySet.has(slotKey)) {
        return { portals: null, block: null };
      }
      const key = `${p.__typename}_${p?.id}`;
      const BlockContentContainer =
        getContentContainer({
          typename: p.__typename,
          ContentContainer,
          isHomePage,
        }) ?? PlainContainer;

      return {
        portals: (
          <Fragment key={`portals_${key}`}>
            <PortalPlace
              component={p}
              vertical="PERSONAL_LOANS"
              baseComponents={visibleBaseComponents}
              id="comparison-table-tabs"
              zOffset={1}
              fullWide={pageType === 'page'}
            />
            <PortalPlace
              id="personal-loan-filter"
              extraStickyIds={['comparison-table-tabs']}
              component={p}
              componentIndex={gaIndexBySlot[index]}
              vertical={
                productBlockTypenames.includes(p.__typename)
                  ? productBlockVertical
                  : vertical
              }
              baseComponents={visibleBaseComponents}
              fullWide={pageType === 'page'}
            />
          </Fragment>
        ),
        block: (
          <Fragment key={key}>
            <BlockContentContainer
              id={`content-${index}`}
              // Test hook: Strapi ids are unique only within a type, so the pair
              // is what addresses one block instance.
              data-block-type={p?.__typename?.replace(
                'ComponentPagesComponent',
                '',
              )}
              data-block-id={p?.id}
            >
              {/* Which slot of the calculation store the block and everything
                  inside it — cells, CTAs, modals — read (CMS-1890). */}
              <CalculationScope moduleId={blockKey(p)}>
                {getComponent({
                  ...props,
                  compProps: p,
                  pathname,
                  metadataProductCard,
                  index: index,
                  gaIndex: gaIndexBySlot[index],
                  brand,
                  productDetailsModalKeys: productDetailsComponentsKeys,
                })}
              </CalculationScope>
            </BlockContentContainer>
          </Fragment>
        ),
      };
    });

  const rendered = renderedBlocks.flatMap(({ portals, block }) => [
    render === 'blocks' ? null : portals,
    render === 'portals' ? null : block,
  ]);

  // A caller passing a range owns the container. Wrapping a slice here would
  // also collapse the sticky roots' containing block from the page to the slice.
  if (renderRange) return <>{rendered}</>;

  return <div className={stackClassName}>{rendered}</div>;
};

const getComponent = ({
  metadataProductCard,
  compProps: p,
  index,
  gaIndex,
  brand,
  productDetailsModalKeys,
  ...rest
}: BlockMapperProps & {
  compProps: CmsComponentProps;
  metadataProductCard: ComponentProductMetadata | undefined;
  brand: BrandId;
  pathname: string;
  productDetailsModalKeys: string[];
  /** The block's visible row on the page — GA component_index (CMS-1693). */
  gaIndex?: number;
}) => {
  switch (p?.__typename) {
    case 'ComponentPagesComponentTextBlock': {
      const textBlock = <PieceOfContent {...p} pushDataLayer={pushDataLayer} />;
      // Audience visibility (CMS-1794): gate only for a non-All audience so
      // ordinary text blocks keep their untouched server render.
      return p.audience && p.audience !== 'ALL' ? (
        <AudienceGate audience={p.audience}>{textBlock}</AudienceGate>
      ) : (
        textBlock
      );
    }
    case 'ComponentPagesComponentTableOfContents':
      return (
        <TableOfContents
          {...p}
          components={rest.baseComponents ?? rest.components}
        />
      );
    case 'ComponentPagesComponentReviewCards':
      return <ReviewCards {...p} />;
    case 'ComponentPagesComponentCustomerReviewsBlockComponent':
      return <CustomerReviewsBlock {...p} />;
    case 'ComponentPagesComponentQuoteHighlightComponent':
      return <QuoteHighlight {...p} pushDataLayer={pushDataLayer} />;
    case 'ComponentPagesComponentPreComparisonChecklistComponent':
      return <PreComparisonChecklist {...p} />;
    case 'ComponentPagesComponentScenarioAccordionComponent':
      return (
        <ScenarioAccordion
          {...p}
          pushDataLayer={pushDataLayer}
          componentIndex={String(gaIndex ?? '')}
        />
      );
    case 'ComponentPagesComponentImageCarousel':
      return (
        <ImageCarousel
          {...p}
          slug={rest.slug}
          pushDataLayer={pushDataLayer}
          componentIndex={String(gaIndex ?? '')}
        />
      );
    case 'ComponentPagesComponentBlueCta':
      return <BlueCTA {...p} />;
    case 'ComponentPagesComponentSelectedProducts':
      return (
        <SelectedProducts
          {...p}
          vertical={rest.vertical}
          slug={rest.slug}
          pageType={rest.pageType}
          index={index}
          brand={brand}
        />
      );
    case 'ComponentPagesComponentSimilarProducts':
      return (
        <SimilarProducts
          {...p}
          slug={rest.slug}
          pushDataLayer={pushDataLayer}
        />
      );
    case 'ComponentPagesComponentProductCardGridComponent':
      return <ProductCardGridComponent {...p} />;
    case 'ComponentPagesComponentRewardCardGridComponent':
      return (
        <RewardCardGridComponent
          {...p}
          componentIndex={String(gaIndex ?? '')}
        />
      );
    case 'ComponentPagesComponentNestedContent':
      return <FAQ {...p} pushDataLayer={pushDataLayer} />;
    case 'ComponentPagesComponentRelevantArticle':
      return <RelevantArticle {...p} componentIndex={String(gaIndex ?? '')} />;
    case 'ComponentPagesComponentInfoCards':
      return <InfoCards {...p} />;
    case 'ComponentPagesComponentInfoCardsFaq':
      return <InfoCardsFAQ {...p} />;
    case 'ComponentPagesComponentPrefilledBlock':
      return (
        <PrefilledBlock
          {...p}
          vertical={rest.vertical}
          pageType={rest.pageType}
          slug={rest.slug}
          ContentContainer={rest.ContentContainer}
          urlPrefix={rest.urlPrefix}
          addressFlags={rest.addressFlags}
        />
      );
    case 'ComponentPagesComponentParagraph':
      return <Paragraph {...p} />;
    case 'ComponentPagesComponentDisclaimerComponent':
      return brand === 'singsaver' ? <ShortDisclaimer {...p} /> : null;
    case 'ComponentPagesComponentWhiteTextBlock':
      return <TheWhiteBoard {...p} />;
    case 'ComponentPagesComponentExpandableCard':
      return <ExpandableCard {...p} />;
    case 'ComponentPagesComponentCallToAction':
      return <CallToAction {...p} />;
    case 'ComponentPagesComponentCtaSectionBlockComponent':
      return <CtaSectionBlock {...p} />;
    case 'ComponentPagesComponentRedirectTiles':
      return <RedirectTiles {...p} />;
    case 'ComponentPagesComponentHeading':
      return <Heading {...p} />;
    case 'ComponentPagesComponentFeedbackAndShare':
      return <FeedbackAndShare {...p} url={rest.pathname} />;
    case 'ComponentPagesComponentHeaderWithTabs':
      return <HeaderWithTabs {...p} />;
    case 'ComponentPagesComponentSwiperCards':
      return <SwiperCards {...p} />;
    case 'ComponentPagesComponentRpCategories':
      return <RpCategories {...p} ContentContainer={rest.ContentContainer} />;
    case 'ComponentPagesComponentPromotionCampaings':
      return (
        <CreditCardPromotion
          {...p}
          brand={brand}
          vertical={rest.vertical}
          blockIndex={index}
          slug={rest.slug}
          tableConfig={rest.components.find(
            (f) => f.__typename === 'ComponentPagesComponentComparisonTable',
          )}
        />
      );
    case 'SingleProductPromotionsComponent':
      return (
        <SingleProductPromotionWrapper
          id={p.id}
          visible={p.visible}
          featureColor={p.featureColor}
          backgroundColor={p.backgroundColor}
          product={p.product}
          additionalCampaign={p.additionalCampaign}
          howToApply={p.howToApply}
          vertical={rest.vertical}
          pushDataLayer={pushDataLayer}
        />
      );
    case 'ComponentPagesComponentHeroCampaingFeature':
      return <HeroCampaingFeature {...p} />;
    case 'ComponentPagesComponentTabbedComponent':
      return <CollapsibleContentTabbed {...p} />;
    case 'SummaryProductsComponent':
      return <SummaryCards metadata={metadataProductCard} summaryConfig={p} />;
    case 'ComponentPagesComponentComparisonTable':
      return (
        <ComparisonTableMap
          tableConfig={p}
          brand={brand}
          metadata={metadataProductCard}
          slug={rest.slug}
          blockIndex={index}
        />
      );
    case 'ComponentPagesComponentCardListingPosition':
      return (
        <CompareListingProvider
          enabled={p.comparison_enabled !== false}
          componentIndex={String(gaIndex ?? '')}
        >
          <CardListingWrapper
            metadata={metadataProductCard}
            tableConfig={rest.components.find(
              (f) => f.__typename === 'ComponentPagesComponentComparisonTable',
            )}
            pageType={rest.pageType}
            cardPosition={p}
            url={rest.slug ?? ''}
            calculateError={false}
            pushDataLayer={pushDataLayer}
            productDetailsModalKeys={productDetailsModalKeys}
          />
        </CompareListingProvider>
      );
    case 'ComponentPagesComponentCcCardListing':
      return (
        <CompareListingProvider
          enabled={p.comparison_enabled !== false}
          componentIndex={String(gaIndex ?? '')}
        >
          <CcCardListingWrapper
            listing={p}
            metadata={metadataProductCard}
            pushDataLayer={pushDataLayer}
            componentIndex={String(gaIndex ?? '')}
            url={rest.slug ?? ''}
          />
        </CompareListingProvider>
      );
    case 'ComponentPagesComponentSearchStickyFooterComponent':
      return <SearchStickyFooter {...p} brand={brand} />;
    case 'ComponentPagesComponentAnchorStickyFooterComponent':
      return <AnchorStickyFooterComponent {...p} />;
    case 'ComponentPagesComponentGridModuleComponent':
      return (
        <FeaturesGridModule {...p} ContentContainer={rest.ContentContainer} />
      );
    case 'ComponentPagesComponentSectionalHeaderComponent':
      return <SectionalHeader {...p} />;
    case 'ComponentPagesProviderList':
      return <ProviderList {...p} />;
    case 'ComponentPagesComponentMiniProductHighlightComponent':
      return <MiniProductHighlight {...p} />;
    case 'ComponentPagesComponentMultiProductBlockComponent':
      return (
        <MultiProductBlockWrapper
          mode="single"
          block={p}
          pageType={rest.pageType}
          vertical={rest.vertical}
          slug={rest.slug}
        />
      );
    case 'ComponentPagesComponentVerticalArticlesComponent':
      return <VerticalArticles {...p} />;
    case 'ComponentPagesComponentSecondaryCtaComponent':
      return p.audience && p.audience !== 'ALL' ? (
        <AudienceGate audience={p.audience}>
          <SecondaryCTA {...p} />
        </AudienceGate>
      ) : (
        <SecondaryCTA {...p} />
      );
    case 'ComponentPagesComponentProsAndConsComponent':
      return <ProsAndCons {...p} />;
    case 'ComponentPagesComponentSimpleStickyFooterComponent':
      return <SimpleStickyFooter {...p} />;
    case 'ComponentPagesComponentTestimonialsComponent':
      return <HPReviewCards {...p} />;
    case 'ComponentPagesComponentFinancialProvidersComponent':
      return <LogoCarousel {...p} />;
    case 'ComponentPagesComponentTopPicksHpComponent':
      return <TopPicksModule {...p} />;
    case 'ComponentPagesComponentEmbeddedScriptComponent':
      return <EmbeddedScript {...p} />;
    case 'ComponentPagesComponentPreferredSourceButtonComponent':
      return <PreferredSourceButton {...p} />;
    case 'ComponentPagesComponentVideoBlockComponent':
      return <VideoBlock {...p} />;
    case 'ComponentPagesComponentCompanyValuesComponent':
      return <CounterModule {...p} />;
    case 'ComponentPagesComponentLicensedAndGroupComponent':
      return <AboutModule {...p} />;
    case 'ComponentPagesComponentHPArticlesModule':
      return <ArticlesHPModule {...p} />;
    case 'ComponentPagesComponentNewsletterSubscriptionHpComponent':
      return <NewsletterSubscription {...p} />;
    case 'ComponentPagesComponentImage':
      return <ImageCmp {...p} pageType={rest.pageType} />;
    case 'ComponentPagesComponentStandoutTextBlockComponent':
      return <StandoutTextBlock {...p} />;
    case 'ComponentPagesComponentContactUsComponent':
      return (
        <WhatsappStickyModule
          {...p}
          pushDataLayer={pushDataLayer}
          vertical={rest.vertical}
        />
      );
    case 'ComponentPagesComponentInsuranceStepperComponent':
      return <InsuranceStepper {...p} />;
    case 'ComponentPagesComponentFlexibleHeroBlockComponent':
      // it renders in FlexibleHeroBlockWrapper before all another content
      return null;
    case 'TravelPolicySearchComponent':
      return <TravelPolicySearch {...p} metaConfig={metadataProductCard} />;
    case 'TrustStripComponent':
      return <TrustStripWrapper {...p} />;
    case 'ComponentPagesComponentPlanTierSelectorComponent':
      return <PlanTierSelectorWrapper component={p} />;
    case 'ComponentPagesComponentPolicySummaryCardComponent':
      return <PolicySummaryCardWrapper {...p} />;
    case 'ComponentPagesComponentProgressStepperComponent':
      return <ProgressStepperWrapper {...p} />;
    case 'ComponentPagesComponentBenefitComparisonTableComponent':
      return <BenefitComparisonTableWrapper component={p} />;
    case 'ComponentPagesComponentAioModuleComponent':
      return (
        <AioModule
          {...p}
          slug={rest.slug}
          pushDataLayer={pushDataLayer}
          componentIndex={String(gaIndex ?? '')}
        />
      );
    case 'ComponentPagesComponentPersonalLoanCalculator':
      return <PersonalLoanCalculator {...p} />;
    case 'ComponentPagesComponentTableBuilderComponent':
      return (
        <TableBuilderWrapper
          {...p}
          pushDataLayer={pushDataLayer}
          pageType={rest.pageType}
          vertical={rest.vertical}
        />
      );
    case 'ComponentPagesComponentQuickAnswerComponent':
      return (
        <QuickAnswerWrapper
          {...p}
          pageType={rest.pageType}
          vertical={rest.vertical}
          blockIndex={index}
          slug={rest.slug}
        />
      );
    case 'ProductDetailsComponent':
      return (
        <ProductDetailsBlock
          {...p}
          vertical={rest.vertical}
          pushDataLayer={pushDataLayer}
          pageType={rest.pageType}
        />
      );
    default:
      console.warn(`Unknown component type: ${p?.__typename}`);

      return null;
  }
};
